import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
// store para recoger el token y para setear los menús
import { useAuthStore } from "@/stores/auth";
import { useAppSidebarMenuStore } from "@/stores/app-sidebar-menu";
import { useAppOptionStore } from "@/stores/app-option";
// endpoints
import { getEndpoints } from "@/services/apiServices/endpoints";
// axios
import axios from "axios";

const routes: Array<RouteRecordRaw> = [
  // 1️⃣ Login específico para kiosco (PIN, QR…)
  {
    path: "/kiosk-login",
    name: "kiosk-login",
    component: () =>
      import(
        /* webpackChunkName: "KioskLoginView" */ "@/views/KioskLoginView.vue"
      ),
    meta: { requireAuth: false, kiosk: true },
  },

  // 2️⃣ Vista principal de fichaje en modo kiosco
  {
    path: "/kiosk",
    name: "kiosk",
    component: () =>
      import(
        /* webpackChunkName: "KioskFichajeView" */ "../views/checkTime/kiosk/KioskCheckTime.vue"
      ),
    meta: { requireAuth: true, kiosk: true },
  },
  // se redirige cualquiero otra ruta dentro de /kiosk/ al login
  {
    path: "/kiosk/:pathMatch(.*)*",
    redirect: { name: "kiosk-login" },
  },
  // Redirige cualquier otra ruta desconocida de la PWA normal
  {
    path: "/:pathMatch(.*)*",
    redirect: (to) => {
      const token = localStorage.getItem("token");
      // si no hay token, al login; si hay, a la landing
      return token ? "/inicio" : "/login";
    },
  },
  {
    path: "/",
    redirect: () => {
      // Si estamos bajo /kiosk/, al login-kiosk
      if (window.location.pathname.startsWith("/kiosk")) {
        return "/kiosk-login";
      }
      // Normal PWA:
      const remember = JSON.parse(
        localStorage.getItem("rememberSetup") || "false",
      );
      const last = localStorage.getItem("lastPath");
      // si eligió “recordar” y hay lastPath válida, íbamos ahí
      if (remember && last) {
        return last;
      }
      // en otro caso, al login
      return "/login";
    },
  },
  {
    path: "/login",
    name: "login",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/LoginView.vue"),
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/restablecer-password/:token",
    name: "restablecer-password",
    component: () =>
      import(
        /* webpackChunkName: "password-restore" */ "../views/PasswordRestoreView.vue"
      ),
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/inicio",
    name: "landing",
    component: () =>
      import(/* webpackChunkName: "landing" */ "../views/LandingView.vue"),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/agenda",
    component: () =>
      import(
        /* webpackChunkName: "Schedule" */ "../views/schedule/ScheduleRouting.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/fichajes",
    component: () =>
      import(
        /* webpackChunkName: "CheckTime" */ "../views/checkTime/CheckTimeRouting.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/contactos",
    component: () =>
      import(
        /* webpackChunkName: "ContactsBook" */ "../views/contactsBook/ContactsBookRouting.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/gastos",
    component: () =>
      import(
        /* webpackChunkName: "ExpensesManager" */ "../views/expensesManager/ExpensesManagerRouting.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/transporte",
    component: () =>
      import(
        /* webpackChunkName: "TransportManager" */ "../views/transportManager/TransportManagerRouting.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/mensajeria",
    component: () =>
      import(
        /* webpackChunkName: "Messenger" */ "../views/messenger/MessengerRouting.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/gerencia",
    component: () =>
      import(
        /* webpackChunkName: "Management" */ "../views/management/ManagementEntry.vue"
      ),
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "error",
    component: () =>
      import(/* webpackChunkName: "error" */ "../views/ErrorView.vue"),
    meta: {
      requireAuth: false,
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});
// validación del token
const validateToken = async (token: string): Promise<boolean> => {
  const lang = localStorage.getItem("language");
  let config = {
    headers: {
      "Accept-Language": lang,
    },
  };
  // endpoint
  const endpoints = getEndpoints();
  const URL = endpoints.checkToken;
  // envio del token actual
  let userToken = {
    token: token,
  };
  const response = await axios.post(URL, userToken, config);
  if (response.data)
    return true; // si la respuesta es true el token es válido
  else return false;
};
// PROTECCIÓN DE RUTAS
// con el método beforeEach especificamos que queremos hacer antes de ir a cada ruta
// para ello establecemos una GUARDA.
// se reciben tres argumentos:
//  - to: a donde se pretende navegar el usuario
//  - from: de donde se viene
//  - next: hacia donde va a ir el usuario
router.beforeEach(async (to, from, next) => {
  // instancia de la store
  const authStore = useAuthStore();

  // Si la ruta es kiosco
  if (to.meta.kiosk) {
    const kioskAuth = sessionStorage.getItem("kiosk-authenticated") === "true";
    // Si vamos al login-kiosk o ya estamos autenticados, dejamos pasar
    if (to.name === "kiosk-login" || kioskAuth) {
      return next();
    }
    // Si no, forzamos a /kiosk-login
    return next({ name: "kiosk-login" });
  }

  const appOption = useAppOptionStore();
  // token
  const token = localStorage.getItem("token") || authStore.getJwt || "";
  // se comprueba si existe token
  const auth = token !== "";
  // se guarda si a donde quiere ir el usuario requiere autenticación
  const needAuth = to.meta.requireAuth;
  // se comprueba si es una recarga de página o si el usuario cearro y volvió a abrir la pestaña o ventana
  const reloadDetected = localStorage.getItem("reloadDetected");
  // si se requiere autenticación y el usuario no esta autenticado (no existe token) se le dirigue a login para que se autentique
  if (needAuth && !auth) {
    next("login");
  } else if (needAuth && auth) {
    if (reloadDetected) {
      // Si se detectó una recarga se valida el token
      try {
        // se valida el token
        const isValidToken = await validateToken(token);
        // si el token es válido
        if (isValidToken) {
          // nombre e icono del menú en el que estaba el usuario
          appOption.appSelectedMenu =
            localStorage.getItem("selectedMenu") || "";
          appOption.appSelectedMenuIcon =
            localStorage.getItem("selectedMenuIcon") || "";
          // se maneja el menú y otros estados después de la validación del token
          const appSidebarMenuStore = useAppSidebarMenuStore();
          appSidebarMenuStore.setMenuItems();
          // se limpian las flags del localstorage
          localStorage.removeItem("reloadDetected");
          localStorage.removeItem("selectedMenu");
          localStorage.removeItem("selectedMenuIcon");
          //next(); // por orden de Andres se navega siempre a la landing si se eligio recordar, en vez de a donde estaba al cerrar o refrescar
          next("/inicio");
        } else {
          await authStore.logOut();
          next("/login");
        }
      } catch (error) {
        console.error("Error al validar el token:", error);
        authStore.logOut();
        next("/login");
      }
    } else {
      next();
    }
    // si no se requiere autenticación se le permite ir a donde pretende navegar
  } else {
    next();
  }
});

export { routes };
export default router;
